import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, OnInit } from '@angular/core';
import { TimeLimitationOption } from '../../models';
import { TrustCenterFacadeService } from '@anecdotes/shared-trust-center';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

export const DEFAULT_TIME_LIMITATION = 8;

@Component({
  selector: 'app-access-time-limitation',
  templateUrl: './access-time-limitation.component.html',
  styleUrls: ['./access-time-limitation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AccessTimeLimitationComponent implements OnInit {
  @HostBinding('class')
  private readonly classes = 'flex w-full flex-col gap-4';

  hasAccessTimeLimitation: boolean;
  selectedTimeLimit: TimeLimitationOption;
  defaultTimeLimitationOption: TimeLimitationOption;
  timeLimitationOptions: TimeLimitationOption[];

  constructor(
    private trustCenterFacade: TrustCenterFacadeService,
    private translate: TranslateService,
    private cd: ChangeDetectorRef,
  ) {}

  async ngOnInit(): Promise<void> {
    await this.initializeTimeLimitation();
    this.cd.detectChanges();
  }

  buildTranslationKey(key: string): string {
    return `trustCenter.settings.automations.accessTimeLimitation.${key}`;
  }

  async updateTimeLimitationFlag(hasAccessTimeLimitation: boolean): Promise<void> {
    const previousFlag = this.hasAccessTimeLimitation;
    this.hasAccessTimeLimitation = hasAccessTimeLimitation;
    this.cd.detectChanges();

    try {
      await this.trustCenterFacade.updateSettings({
        is_custom_period_in_weeks: hasAccessTimeLimitation,
      });
    } catch {
      this.hasAccessTimeLimitation = previousFlag;
      this.cd.detectChanges();
    }
  }

  async updateTimeLimitation(option: TimeLimitationOption): Promise<void> {
    const previousValue = this.selectedTimeLimit;
    this.selectedTimeLimit = option;
    this.cd.detectChanges();

    try {
      await this.trustCenterFacade.updateSettings({ user_access_period_in_weeks: option.id });
    } catch {
      this.selectedTimeLimit = previousValue;
      this.cd.detectChanges();
    }
  }

  idLimitationsSelector(option: TimeLimitationOption): number {
    return option.id;
  }

  displayLimitationsSelector(option: TimeLimitationOption): string {
    return option.value;
  }

  private async initializeTimeLimitation(): Promise<void> {
    const numberOfWeeks = await firstValueFrom(this.trustCenterFacade.getAccessTimeLimitation());
    const hasAccessTimeLimitation = await firstValueFrom(this.trustCenterFacade.hasAccessTimeLimitation());

    this.initializeTimeLimitationOptions();

    this.hasAccessTimeLimitation = hasAccessTimeLimitation;
    this.defaultTimeLimitationOption = this.getTimeLimitationOptionById(DEFAULT_TIME_LIMITATION);
    this.selectedTimeLimit = this.getTimeLimitationOptionById(numberOfWeeks) || this.defaultTimeLimitationOption;
  }

  private initializeTimeLimitationOptions(): void {
    const weeks = this.translate.instant(this.buildTranslationKey(`timeLimitations.weeks`));
    const weekOptions = [2, 4, 6, 8];

    this.timeLimitationOptions = weekOptions.map((numberOfWeeks) => {
      return {
        id: numberOfWeeks,
        value: `${numberOfWeeks} ${weeks}`,
      };
    });
  }

  private getTimeLimitationOptionById(optionId: number): TimeLimitationOption | undefined {
    if (!optionId) {
      return;
    }

    return this.timeLimitationOptions.find(({ id }) => id === optionId);
  }
}
